import React, { useState, useEffect } from 'react';
import { X, Filter } from 'lucide-react';

const FilterPanel = ({ filters, onFilterChange, instances }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [filterOptions, setFilterOptions] = useState({
    environments: [],
    projects: [],
    owners: [],
    states: []
  });

  useEffect(() => {
    // 인스턴스 데이터에서 필터 옵션 추출
    const environments = [...new Set(instances
      .map(i => i.tags?.Environment)
      .filter(Boolean)
    )].sort();
    
    const projects = [...new Set(instances
      .map(i => i.tags?.Project)
      .filter(Boolean)
    )].sort();
    
    const owners = [...new Set(instances
      .map(i => i.tags?.Owner)
      .filter(Boolean)
    )].sort();
    
    const states = [...new Set(instances
      .map(i => i.state)
      .filter(Boolean)
    )].sort();

    setFilterOptions({
      environments,
      projects,
      owners,
      states
    });
  }, [instances]);

  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...localFilters,
      [key]: value
    };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      state: '',
      environment: '',
      project: '',
      owner: ''
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const activeFilterCount = Object.values(localFilters).filter(Boolean).length;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Filter size={18} className="text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900">필터</h3>
          {activeFilterCount > 0 && (
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
              {activeFilterCount}개 적용
            </span>
          )}
        </div>
        
        {activeFilterCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1"
          >
            <X size={16} />
            <span>모두 지우기</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 상태 필터 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            상태
          </label>
          <select
            value={localFilters.state}
            onChange={(e) => handleFilterChange('state', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">모든 상태</option>
            {filterOptions.states.map(state => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </div>

        {/* 환경 필터 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            환경
          </label>
          <select
            value={localFilters.environment}
            onChange={(e) => handleFilterChange('environment', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">모든 환경</option>
            {filterOptions.environments.map(env => (
              <option key={env} value={env}>
                {env}
              </option>
            ))}
          </select>
        </div>

        {/* 프로젝트 필터 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            프로젝트
          </label>
          <select
            value={localFilters.project}
            onChange={(e) => handleFilterChange('project', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">모든 프로젝트</option>
            {filterOptions.projects.map(project => (
              <option key={project} value={project}>
                {project}
              </option>
            ))}
          </select>
        </div>

        {/* 소유자 필터 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            소유자
          </label>
          <select
            value={localFilters.owner}
            onChange={(e) => handleFilterChange('owner', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">모든 소유자</option>
            {filterOptions.owners.map(owner => (
              <option key={owner} value={owner}>
                {owner}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 필터 요약 */}
      {activeFilterCount > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            활성 필터: {Object.entries(localFilters)
              .filter(([_, value]) => value)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ')}
          </p>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;